import React, { useState, useEffect } from 'react';
import { Search, Filter, FileText, Building2, CreditCard } from 'lucide-react';
import { AdminSubscriptionInvoiceResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';

const SubscriptionInvoices: React.FC = () => {
  const [invoices, setInvoices] = useState<AdminSubscriptionInvoiceResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    status: '' as '' | 'paid' | 'unpaid' | 'expired',
    companyId: '',
    subscriptionId: ''
  });

  useEffect(() => {
    loadInvoices();
  }, [page, perPage, search, filters]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      // Convert empty strings to undefined for API
      const apiFilters = {
        status: filters.status === '' ? undefined : filters.status,
        companyId: filters.companyId === '' ? undefined : filters.companyId,
        subscriptionId: filters.subscriptionId === '' ? undefined : filters.subscriptionId,
      };
      const response = await api.getSubscriptionInvoices(page, perPage, search, apiFilters);
      setInvoices(response.data);
      setTotal(response.meta.total);
    } catch (error) {
      console.error('Error loading subscription invoices:', error);
      setInvoices([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleFiltersClear = () => {
    setFilters({
      status: '',
      companyId: '',
      subscriptionId: ''
    });
    setPage(1);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'unpaid':
        return 'bg-yellow-100 text-yellow-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return '✓';
      case 'unpaid':
        return '⏳';
      case 'expired':
        return '✗';
      default:
        return '?';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription Invoices</h1>
        <p className="text-gray-600">Manage and view all subscription invoices</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFiltersChange({ ...filters, status: e.target.value as '' | 'paid' | 'unpaid' | 'expired' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Company Filter */}
          <div>
            <input
              type="text"
              placeholder="Company ID..."
              value={filters.companyId}
              onChange={(e) => handleFiltersChange({ ...filters, companyId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Subscription Filter */}
          <div>
            <input
              type="text"
              placeholder="Subscription ID..."
              value={filters.subscriptionId}
              onChange={(e) => handleFiltersChange({ ...filters, subscriptionId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Active Filters */}
        {(filters.status || filters.companyId || filters.subscriptionId) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {filters.status && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Status: {filters.status}
                  <button
                    onClick={() => handleFiltersChange({ ...filters, status: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.companyId && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Company: {filters.companyId}
                  <button
                    onClick={() => handleFiltersChange({ ...filters, companyId: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {filters.subscriptionId && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  Subscription: {filters.subscriptionId}
                  <button
                    onClick={() => handleFiltersChange({ ...filters, subscriptionId: '' })}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={handleFiltersClear}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Invoices List */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Loading invoices..." />
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {search || filters.status || filters.companyId || filters.subscriptionId
              ? 'Try adjusting your search or filters.'
              : 'No subscription invoices have been created yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                             <thead className="bg-gray-50">
                 <tr>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Invoice
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Company
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Subscription
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Amount
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Status
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Payment Gateway
                   </th>
                   <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     Date
                   </th>
                 </tr>
               </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </div>
                        <div className="text-sm text-gray-500">{invoice.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invoice.company.companyName}
                          </div>
                          <div className="text-sm text-gray-500">{invoice.company.companyAdminEmail}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invoice.subscription.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {invoice.totalMonth} month{invoice.totalMonth > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(invoice.grandTotal)}
                        </div>
                        {invoice.discount > 0 && (
                          <div className="text-sm text-gray-500">
                            Discount: {formatCurrency(invoice.discount)}
                          </div>
                        )}
                      </div>
                    </td>
                                         <td className="px-6 py-4 whitespace-nowrap">
                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                         {getStatusIcon(invoice.status)} {invoice.status}
                       </span>
                       {invoice.paidAt && (
                         <div className="text-xs text-gray-500 mt-1">
                           Paid: {formatDate(invoice.paidAt)}
                         </div>
                       )}
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap">
                       <div>
                         {invoice.paymentGateway && (
                           <div className="text-sm font-medium text-gray-900">
                             {invoice.paymentGateway}
                           </div>
                         )}
                         {invoice.paymentGatewayTableId && (
                           <div className="text-sm text-gray-500">
                             ID: {invoice.paymentGatewayTableId}
                           </div>
                         )}
                         {!invoice.paymentGateway && !invoice.paymentGatewayTableId && (
                           <div className="text-sm text-gray-400">
                             Not specified
                           </div>
                         )}
                       </div>
                     </td>
                     <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       {formatDate(invoice.createdAt)}
                     </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && invoices.length > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={page}
            total={total}
            perPage={perPage}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SubscriptionInvoices;
